import { Box, Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { InputLabelText } from '@/components/form/baseInputs/InputLabel';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import {
  FormAwareSelectInput,
  FormAwareSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useRemainingCharacters } from '@/components/form/hooks/useRemainingCharacters.hook';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { getMaxLengthValidation } from '@/components/utils/validators';
import { SuggestableFormAwareInputTestamentaryEntity } from '@/modules/aiSuggestions/components/forms/SuggestableFormAwareInputTestamentaryEntity';
import { useTaxStatusAssetLocationOptions } from '@/modules/entities/TaxStatusSubform/hooks/useTaxStatusAssetLocationOptions';
import {
  GST_STATUS_ITEMS,
  MIXED_GST,
} from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.constants';
import { AiSuggestionKind, TestamentaryEntityKind } from '@/types/schema';
import { validateNumberRange } from '@/utils/validators/validateNumberRange';

import {
  StateRecipientsContextualHelp,
  SubtrustCreatedFromSuggestableInput,
} from '../components';
import {
  getTestamentaryEntityKindOptions,
  GST_STATUS_ELIGIBLE_KINDS,
  TESTAMENTARY_ENTITY,
} from '../EditTestamentaryEntitySplitScreen.constants';
import {
  EditTestamentaryEntityFormField,
  EditTestamentaryEntityFormShape,
} from '../EditTestamentaryEntitySplitScreen.types';
import { useAccountOwnerOptions } from '../hooks/useAccountOwnerOptions';

const ASSET_LOCATION_COLUMN_WIDTH = '204px';
const MAX_DESCRIPTION_LENGTH = 2048;

export function BasicInformationSection() {
  const { control } = useFormContext<EditTestamentaryEntityFormShape>();

  const { assetLocationOptions } = useTaxStatusAssetLocationOptions({
    fieldNamePrefix: TESTAMENTARY_ENTITY,
  });

  const gstStatus = useWatch({
    control,
    name: `${TESTAMENTARY_ENTITY}.gstStatus`,
  });

  const selectedTEKind = useWatch({
    control,
    name: `${TESTAMENTARY_ENTITY}.testamentaryEntityKind`,
  });

  const isInheritedRetirementAccount =
    selectedTEKind === TestamentaryEntityKind.InheritedRetirementAccount;

  const { options: accountOwnerOptions } = useAccountOwnerOptions({
    enabled: isInheritedRetirementAccount,
  });

  const showGstStatus = GST_STATUS_ELIGIBLE_KINDS.has(
    selectedTEKind as TestamentaryEntityKind
  );

  const { helpText: descriptionHelpText } = useRemainingCharacters(
    `${TESTAMENTARY_ENTITY}.entityDetailsNotes` as const satisfies EditTestamentaryEntityFormField,
    MAX_DESCRIPTION_LENGTH
  );

  return (
    <Stack spacing={2}>
      <Card sx={{ p: 2 }} variant="filled">
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTextInput<EditTestamentaryEntityFormShape>
              label="Entity name"
              fieldName={
                `${TESTAMENTARY_ENTITY}.displayName` as const satisfies EditTestamentaryEntityFormField
              }
              control={control}
              required
              testId="TestamentaryEntityForm-displayName"
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareSelectInput<EditTestamentaryEntityFormShape>
              fieldName={
                `${TESTAMENTARY_ENTITY}.testamentaryEntityKind` as const satisfies EditTestamentaryEntityFormField
              }
              label="Entity type"
              control={control}
              options={getTestamentaryEntityKindOptions(null)}
              required
              data-testid="TestamentaryEntityModal-testamentaryEntityKind"
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem>
            <SubtrustCreatedFromSuggestableInput />
          </FormLayoutItem>
        </FormLayoutRow>
        {isInheritedRetirementAccount && (
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareTypeaheadSelectInput<EditTestamentaryEntityFormShape>
                fieldName={
                  `${TESTAMENTARY_ENTITY}.accountOwnerId` as const satisfies EditTestamentaryEntityFormField
                }
                label="Account owner"
                control={control}
                options={accountOwnerOptions}
                placeholder="Search individuals"
              />
            </FormLayoutItem>
          </FormLayoutRow>
        )}
        {showGstStatus && (
          <FormLayoutRow>
            <FormLayoutItem>
              <SuggestableFormAwareInputTestamentaryEntity<
                EditTestamentaryEntityFormShape,
                FormAwareSelectInputProps<EditTestamentaryEntityFormShape>
              >
                FormAwareElement={FormAwareSelectInput}
                suggestionKind={AiSuggestionKind.SubtrustGstStatus}
                control={control}
                fieldName={
                  `${TESTAMENTARY_ENTITY}.gstStatus` as const satisfies EditTestamentaryEntityFormField
                }
                label="GST status"
                options={GST_STATUS_ITEMS}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        )}
        {showGstStatus && gstStatus === MIXED_GST && (
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareFormattedNumberInput<EditTestamentaryEntityFormShape>
                control={control}
                fieldName={
                  `${TESTAMENTARY_ENTITY}.inclusionRatio` as const satisfies EditTestamentaryEntityFormField
                }
                label="GST inclusion ratio"
                required
                fixedDecimalScale
                decimalScale={4}
                isDecimalJSInput
                validation={{
                  minMax: validateNumberRange({
                    min: 0.0001,
                    max: 0.9999,
                  }),
                }}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        )}
      </Card>
      {/* Asset location is always shown in the edit modal */}
      <Card sx={{ p: 2 }} variant="filled">
        <Stack spacing={2}>
          <Typography variant="title-s">Asset location</Typography>
          <Stack direction="row" width="100%">
            <Stack justifyContent="flex-end" spacing={1}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box width="60px" />
                <InputLabelText
                  width="180px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  In estate of surviving spouse
                </InputLabelText>
                <InputLabelText
                  width="180px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  Out of estate of surviving spouse
                </InputLabelText>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <InputLabelText width="60px">Federal</InputLabelText>
                <FormAwareRadioGroup<EditTestamentaryEntityFormShape>
                  sx={{ flexFlow: 'nowrap' }}
                  labelSx={{ width: ASSET_LOCATION_COLUMN_WIDTH }}
                  row={true}
                  options={assetLocationOptions[0]?.options || []}
                  fieldName={
                    `${TESTAMENTARY_ENTITY}.estateInclusionStatus` satisfies EditTestamentaryEntityFormField
                  }
                  control={control}
                />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <InputLabelText width="60px">
                  <Stack direction="row" spacing={0.5}>
                    State
                    <StateRecipientsContextualHelp />
                  </Stack>
                </InputLabelText>
                <FormAwareRadioGroup<EditTestamentaryEntityFormShape>
                  sx={{ flexFlow: 'nowrap' }}
                  labelSx={{ width: ASSET_LOCATION_COLUMN_WIDTH }}
                  row={true}
                  options={assetLocationOptions[0]?.options || []}
                  fieldName={
                    `${TESTAMENTARY_ENTITY}.survivingSpouseStateInEstateStatus` satisfies EditTestamentaryEntityFormField
                  }
                  control={control}
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Card>
      <FormAwareTextInput<EditTestamentaryEntityFormShape>
        control={control}
        label="Internal notes"
        fieldName={
          `${TESTAMENTARY_ENTITY}.entityDetailsNotes` as const satisfies EditTestamentaryEntityFormField
        }
        multiline
        rows={4}
        resizable
        helpText={descriptionHelpText}
        validation={{
          maxLength: (v) =>
            getMaxLengthValidation(MAX_DESCRIPTION_LENGTH)(v as string),
        }}
      />
    </Stack>
  );
}
